// frontend/src/modules/monitoramento/components/WidgetMonitoramento.js
import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaSync, FaBuilding, FaCalendar, FaInfoCircle } from 'react-icons/fa';

function WidgetMonitoramento({ empresaId, token }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  const fetchDados = async () => {
    setLoading(true);
    setError('');
    try {
      // Buscar dados da empresa monitorada
      const response = await fetch(`/api/monitoramento/empresa/${empresaId}/status`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar dados de monitoramento');
      }

      const data = await response.json();
      setDados(data);
      setUltimaAtualizacao(new Date());
    } catch (err) {
      setError(err.message);
      // Dados mockados para fallback
      setDados({
        situacao: 'Ativa',
        pendencias: 0,
        ultima_consulta: new Date(),
        status_monitoramento: 'Monitorando',
        cnpj: '11.024.096/0001-44',
        razao_social: 'Empresa Teste LTDA',
        regime_tributario: 'Simples Nacional'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDados();
    // Atualizar a cada 5 minutos
    const interval = setInterval(fetchDados, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [empresaId]);

  const getSituacaoIcon = (situacao) => {
    switch(situacao?.toLowerCase()) {
      case 'ativa':
        return <FaCheckCircle style={{ color: '#28a745', fontSize: '20px' }} />;
      case 'suspensa':
        return <FaExclamationTriangle style={{ color: '#ffc107', fontSize: '20px' }} />;
      case 'inativa':
      case 'baixada':
        return <FaTimesCircle style={{ color: '#dc3545', fontSize: '20px' }} />;
      default:
        return <FaInfoCircle style={{ color: '#6c757d', fontSize: '20px' }} />;
    }
  };

  const getSituacaoColor = (situacao) => {
    switch(situacao?.toLowerCase()) {
      case 'ativa': return '#28a745';
      case 'suspensa': return '#ffc107';
      case 'inativa': return '#dc3545';
      case 'baixada': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  if (loading && !dados) {
    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '24px', marginBottom: '10px' }}>🔄</div>
        <p style={{ color: '#6c757d' }}>Carregando dados da Receita Federal...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#dc3545' }}>
          <FaExclamationTriangle />
          <span>Erro ao carregar dados</span>
        </div>
        <button 
          onClick={fetchDados}
          style={{
            marginTop: '10px',
            padding: '5px 15px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          <FaSync /> Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: 'white', 
      padding: '20px', 
      borderRadius: '8px', 
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      borderLeft: `4px solid ${getSituacaoColor(dados?.situacao)}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaBuilding style={{ color: '#007bff' }} />
          <h3 style={{ margin: 0, fontSize: '16px' }}>📊 Monitoramento Receita Federal</h3>
        </div>
        <button 
          onClick={fetchDados}
          style={{
            background: 'none',
            border: 'none',
            color: '#007bff',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title="Atualizar dados"
        >
          <FaSync size={12} /> Atualizar
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
        {/* Situação */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '12px', 
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>Situação Cadastral</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '5px' }}>
            {getSituacaoIcon(dados?.situacao)}
            <span style={{ 
              fontWeight: 'bold', 
              fontSize: '18px',
              color: getSituacaoColor(dados?.situacao)
            }}>
              {dados?.situacao || 'Não informado'}
            </span>
          </div>
        </div>

        {/* Pendências */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '12px', 
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>Pendências</p>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: (dados?.pendencias || 0) > 0 ? '#dc3545' : '#28a745'
          }}>
            {(dados?.pendencias || 0) > 0 ? `🔴 ${dados.pendencias}` : '✅ Nenhuma'}
          </p>
        </div>

        {/* Regime Tributário */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '12px', 
          borderRadius: '6px'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>Regime Tributário</p>
          <p style={{ margin: '5px 0 0 0', fontWeight: 'bold' }}>
            {dados?.regime_tributario || 'Não informado'}
          </p>
        </div>

        {/* Última Consulta */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '12px', 
          borderRadius: '6px'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>Última Consulta</p>
          <p style={{ margin: '5px 0 0 0', fontSize: '13px' }}>
            {formatDate(dados?.ultima_consulta)}
          </p>
        </div>
      </div>

      {/* Status do Monitoramento */}
      <div style={{ 
        marginTop: '12px', 
        padding: '10px', 
        backgroundColor: '#e8f5e9', 
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: '13px', color: '#2e7d32' }}>
          {dados?.status_monitoramento === 'Monitorando' ? '✅ Monitoramento Ativo' : '⏸️ Monitoramento Inativo'}
        </span>
        {ultimaAtualizacao && (
          <span style={{ fontSize: '11px', color: '#6c757d' }}>
            Atualizado: {formatDate(ultimaAtualizacao)}
          </span>
        )}
      </div>

      {/* Info adicional */}
      <div style={{ 
        marginTop: '10px', 
        fontSize: '12px', 
        color: '#6c757d',
        textAlign: 'center',
        borderTop: '1px solid #f0f0f0',
        paddingTop: '10px'
      }}>
        <FaInfoCircle style={{ marginRight: '5px' }} />
        Dados consultados via API SERPRO - Integra Contador
      </div>
    </div>
  );
}

export default WidgetMonitoramento;